/**
 * ThemeCycleButton
 *
 * Compact single-click cycle through Light → Dark HMI → Legacy → Light.
 * Used in the profile popover / sidebar where surface area is tight.
 * For the "pick one" settings experience, use `ThemeToggle` instead.
 */
import {
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Memory as LegacyIcon,
} from '@mui/icons-material';
import { useThemeStore } from '@/app/stores/themeStore';
import { THEME_LABELS, type ThemeMode } from '@/theme/muiThemes';

const ORDER: ThemeMode[] = ['light', 'dark', 'legacy'];

const THEME_ICON: Record<ThemeMode, React.ReactNode> = {
  light: <LightModeIcon fontSize="small" />,
  dark: <DarkModeIcon fontSize="small" />,
  legacy: <LegacyIcon fontSize="small" />,
};

interface Props {
  expanded?: boolean;
}

export const ThemeCycleButton: React.FC<Props> = ({ expanded = true }) => {
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);

  const nextTheme = ORDER[(ORDER.indexOf(theme) + 1) % ORDER.length];

  const handleClick = () => setTheme(nextTheme);

  if (expanded) {
    return (
      <ListItemButton
        onClick={handleClick}
        sx={{
          minHeight: 44,
          px: 2,
          gap: 1.5,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
          {THEME_ICON[theme]}
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">테마</Typography>
              <Typography variant="caption" color="text.secondary">
                {THEME_LABELS[theme]} → {THEME_LABELS[nextTheme]}
              </Typography>
            </Box>
          }
        />
      </ListItemButton>
    );
  }

  return (
    <Tooltip title={`테마: ${THEME_LABELS[theme]} (클릭 → ${THEME_LABELS[nextTheme]})`} placement="right">
      <ListItemButton
        onClick={handleClick}
        sx={{
          minHeight: 48,
          px: 1,
          justifyContent: 'center',
          color: 'text.secondary',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {THEME_ICON[theme]}
      </ListItemButton>
    </Tooltip>
  );
};
